// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The intake tab: the golden-CSV DROP POINT, plus the provenance panel (the golden source
// this cycle is built on, the period identity, and the state-machine cursor).
//
// The drop point is the only WRITE on this tab, and it does not go through a SECURITY DEFINER
// RPC — there is no write-path RPC in 0003a by design. It posts to the `reporting-golden-ingest`
// edge function, which holds the service-role key, re-asserts can_review_reporting() against
// the caller's own JWT, runs the hard intake gates, and lands the rows. See
// CombinedCsvDropZone + hooks/investment-reporting/useGoldenIngest.
//
// ONE FILE, NO DROPDOWN (CIO, 2026-08-11). The drop point used to ask the human what each file
// was, from an eleven-value picker. It no longer asks: the combined FE export is classified in
// the browser by src/features/investment-reporting, and the classification — counts per class,
// the per-level benchmark tie check, the unclassified residue by name, the basis statement — is
// put on screen BEFORE anything is committed. A structural refusal (a missing peer house, an
// incomplete benchmark ladder) removes the confirm entirely rather than disabling it quietly.
// The old per-file picker is NOT deleted — it is demoted to a collapsed "other files" section at
// the bottom, because the holdings snapshot, the holdings deltas and the ba_trades file are not in
// the performance export at all (they come from the blotter), carry no FE preamble to identify
// themselves from, and are required by reporting_snapshot_precheck. It is demoted AND NARROWED:
// see BLOTTER_KINDS below. Demotion alone left the refusals above bypassable from the same tab.
//
// Everything BELOW the drop point stays read-only — with ONE exception, added deliberately:
// the RunCycleControl in the Pipeline-stage section. That is the START control for the whole
// pipeline (enqueue_reporting_cycle), and this tab is its home because this is the DROP
// POINT: the human who has just landed a period's golden CSVs is the human who then starts
// the run, and this is the only tab that already shows the state-machine cursor the run
// advances. The alternatives were worse — the cycle LIST is per-cycle-agnostic (it creates
// cycles, it does not drive one), and Report/Flags/Artifacts/Gate are review surfaces where
// a money-spending action would be a genuine surprise.
//
// The golden_source block is still written by the compute pipeline (and attested via
// attest_golden_source), not by the drop — so a file landing here does NOT immediately change
// that panel, and this tab does not pretend it does.

import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CheckCircle2, ChevronDown, ChevronRight, Circle } from "lucide-react";
import { EmptyState, LoadingState } from "@/components/research-hub";
import {
  CombinedCsvDropZone,
  formatDay,
  GoldenDropZone,
  GoldenSourcesPanel,
  RunCycleControl,
} from "@/components/investment-reporting";
import { useReportingAccess } from "@/hooks/investment-reporting/useReportingAccess";
import { useFeExportCensus, useSnapshotPrecheck } from "@/hooks/investment-reporting/useGoldenSources";
import type { CycleStage } from "@/types/ir";
import type { CycleContext } from "./ReportingCycleLayout";

// RENDER BEFORE GATE. This mirrors STAGE_ORDER in
// supabase/functions/_shared/reporting-cycle-worker.ts:214, which is the authority on the
// order the cursor actually moves in — and it puts render BEFORE gate, because
// approve_reporting_gate enforces render-before-gate (the approver must bind their signature
// to an already-frozen deck whose hash matches the IR being signed). This array previously
// read ...verify, gate, render, deliver, which drew the two steps the wrong way round and so
// showed a cycle sitting at `render` as though it had already passed the gate.
const STAGES: CycleStage[] = ["snapshot", "compute", "verify", "render", "gate", "deliver"];

// THE ONLY THREE KINDS THE DEMOTED PICKER MAY LAND — and this list is the whole reason that
// section still exists.
//
// Demoting GoldenDropZone was not enough. Its picker was populated from the UNFILTERED
// GOLDEN_KIND_SPECS, which includes fe_portfolio_returns and fe_benchmark_returns — so the exact
// combined CSV that CombinedCsvDropZone REFUSES (42 of the 45 peer constituents, an incomplete
// benchmark ladder) could be re-tagged one section lower and landed with no peer check, no tie
// check, and nothing anywhere recording that a refusal had been overridden. Every claim this file
// makes above about refusals being structural was false while that entry sat in the list: a
// refusal you can walk round from the same tab, in two clicks, is a suggestion.
//
// So the picker is narrowed to the three blotter files the section is FOR. If an FE-kind override
// is ever genuinely wanted it does not belong here — it has to be its own named action, separately
// confirmed, writing the override and the reason for it onto reporting_golden_sources so the
// override is part of the cycle's provenance rather than indistinguishable from a clean landing.
const BLOTTER_KINDS = ["holdings_base_snapshot", "holdings_deltas", "ba_trades"] as const;

// ============================================================================
// The persisted last_error, presented as HISTORY.
//
// reporting_cycles.last_error is the error text from the LAST failed run and it is not cleared
// when the condition that caused it goes away — only a successful gate approval nulls it
// (approve_reporting_gate) . It was being rendered as a bare "Last error: …" line with no date,
// directly under the live stage cursor, which reads as the cycle's CURRENT complaint. For
// 2026-07 it was actively misleading: the stored text names holdings_base_snapshot,
// holdings_deltas and ba_trades as missing_required, and all three landed AFTER that run.
// Correct as history, wrong as presented.
//
// So it is dated, labelled as a past run, and — where the stored text is the snapshot
// precheck's own JSON envelope — checked against the LIVE precheck so the parts that have since
// been fixed are named as fixed. The raw text is still shown verbatim underneath: this narrows
// what the reader should believe, it does not edit the record.
// ============================================================================
function PastRunError({
  lastError,
  recordedAt,
  periodLabel,
}: {
  lastError: string;
  recordedAt?: string | null;
  periodLabel?: string | null;
}) {
  const access = useReportingAccess();
  const precheck = useSnapshotPrecheck(periodLabel, access.canReview);

  // The snapshot failure stores "snapshot not ready: {…}". Pull the envelope back out if it is
  // there; anything else renders as opaque text rather than being guessed at.
  const stale = (() => {
    const open = lastError.indexOf("{");
    const close = lastError.lastIndexOf("}");
    if (open < 0 || close <= open || !precheck.data) return null;
    let parsed: any;
    try {
      parsed = JSON.parse(lastError.slice(open, close + 1));
    } catch {
      return null;
    }
    const wasMissing: string[] = Array.isArray(parsed?.missing_required) ? parsed.missing_required : [];
    if (wasMissing.length === 0) return null;
    const presentNow = new Set(precheck.data.present_kinds ?? []);
    const sinceLanded = wasMissing.filter((k) => presentNow.has(k));
    return sinceLanded.length > 0 ? { wasMissing, sinceLanded } : null;
  })();

  return (
    <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1.5">
      <p className="font-body text-xs font-medium text-foreground">
        Error from the last failed run
        {recordedAt ? (
          <>
            {" "}
            — cycle last updated {formatDay(recordedAt)}{" "}
            <span className="text-muted-foreground">
              ({new Date(recordedAt).toLocaleTimeString()})
            </span>
          </>
        ) : null}
      </p>
      <p className="font-body text-[11px] text-muted-foreground">
        This is kept as history. It is not re-checked, and it is not cleared when the problem is
        fixed — it stays on the cycle until a run succeeds.
      </p>
      {recordedAt && (
        <p className="font-body text-[11px] text-muted-foreground">
          The stamp above is <span className="font-mono">reporting_cycles.updated_at</span>, the
          row&rsquo;s last-modified time. There is no <span className="font-mono">last_error_at</span>{" "}
          column, so this is the closest thing recorded to when the error was written — it is an
          upper bound, not the run&rsquo;s own timestamp, and any later write to the cycle moves it.
        </p>
      )}

      {stale && (
        <p className="font-body text-[11px] text-amber-600 dark:text-amber-400">
          Partly out of date: it lists{" "}
          <span className="font-mono break-all">{stale.sinceLanded.join(", ")}</span> as missing,
          and {stale.sinceLanded.length === 1 ? "that source has" : "those sources have"} since
          landed. Do not read this message as the cycle&rsquo;s current state — the live position
          is in the precheck under &ldquo;Landed sources for this period&rdquo; above.
        </p>
      )}

      <p className="font-body text-[11px] text-destructive break-words whitespace-pre-wrap">
        {lastError}
      </p>
    </div>
  );
}

// ============================================================================
// WHICH FE EXPORTS THIS CYCLE STILL NEEDS.
//
// An FE Performance Table export covers ONE window but EVERY series
// (golden-csv-contract-v1 section 1), so a four-period cycle is FOUR exports sharing
// period_label and as_of_date and differing ONLY in the preamble `from`. Nothing used to say
// so. reporting_snapshot_precheck asks "is this KIND present?" and never "which WINDOWS of it
// are present?", so one file made fe_benchmark_returns present, the precheck went green, and
// the three missing exports surfaced weeks later as a render refusing on unresolved_cell_ratio
// — a message about rendering, for a problem that was an un-uploaded file.
//
// Measured on cycle ec8e622a (2026-07): the benchmark source landed 26 rows, every one
// return_1m; 105 of that cycle's 118 unresolved cells were the benchmark and excess cells for
// q2/m6/y1.
//
// This panel is DESCRIPTIVE. It cannot change `ready` — that is still entirely the precheck's
// verdict — and it adds nothing to missing_required. It exists so the operator is told, in
// time, with the exact date to type into FE.
// ============================================================================
function FeExportCensusPanel({ periodLabel, canRead }: { periodLabel: string; canRead: boolean }) {
  const census = useFeExportCensus(periodLabel, canRead);

  if (!canRead) return null;
  if (census.isLoading) return <LoadingState rows={1} />;
  // A failing RPC must not blank the tab — the drop points above still work without it — but
  // it must not vanish SILENTLY either. This panel's whole job is to answer "have all the
  // exports landed?", and a panel that is simply absent reads as "nothing to report" when it
  // actually means "could not tell". Say which it is, in one muted line.
  if (census.error || !census.data) {
    return (
      <p className="font-body text-xs text-muted-foreground max-w-3xl">
        FE export census unavailable — could not read{" "}
        <span className="font-mono">reporting_fe_export_census</span>
        {census.error instanceof Error ? `: ${census.error.message}` : ""}. This does not block
        the drop points above; it means this panel cannot confirm how many of the period&rsquo;s
        FE exports have landed, not that they are all present.
      </p>
    );
  }

  const c = census.data;
  const complete = c.missing_exports.length === 0;
  const roster = c.fe_kind_roster_source;

  return (
    <section className="space-y-2">
      <h3 className="font-heading text-base font-medium text-foreground">
        FE exports for this period — {c.exports_present} of {c.exports_required} complete
      </h3>
      <p className="font-body text-xs text-muted-foreground max-w-3xl">{c.operator_note}</p>
      {/* Which export defined the roster every other window is judged against. Shown because
          the roster is the one input that can make a landed export read as missing: if the
          "still missing" column names a series your FE report no longer exports, this is the
          file to look at. */}
      {roster && (
        <p className="font-body text-[11px] text-muted-foreground max-w-3xl">
          Roster of {c.fe_kind_roster.length} series measured from{" "}
          <span className="font-mono">{roster.filename ?? `sha ${roster.content_sha256.slice(0, 12)}`}</span>{" "}
          (the most recent <code>{c.primary_metric}</code> export):{" "}
          <span className="font-mono">{c.fe_kind_roster.join(", ")}</span>.
        </p>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left font-body font-medium p-2">Period</th>
                <th className="text-left font-body font-medium p-2">FE &ldquo;from&rdquo;</th>
                <th className="text-left font-body font-medium p-2">FE &ldquo;to&rdquo;</th>
                <th className="text-left font-body font-medium p-2">Still missing</th>
              </tr>
            </thead>
            <tbody>
              {c.exports.map((e) => (
                <tr key={e.metric} className="border-b border-border last:border-0">
                  <td className="p-2 font-body text-foreground">
                    {e.present ? (
                      <CheckCircle2 className="h-3.5 w-3.5 inline mr-1.5 text-primary" aria-hidden />
                    ) : (
                      <span className="inline-block w-3.5 mr-1.5" aria-hidden />
                    )}
                    <code>{e.metric}</code>
                    <span className="text-muted-foreground"> · {e.window_months}m</span>
                  </td>
                  {/* The value an operator types into FE: the PRIOR priced date, one day
                      before the window start, because FE prices priced-date to priced-date. */}
                  <td className="p-2 font-mono tabular-nums text-foreground">{e.fe_price_range_from}</td>
                  <td className="p-2 font-mono tabular-nums text-foreground">{e.fe_price_range_to}</td>
                  <td className="p-2 font-body text-muted-foreground break-words">
                    {e.present ? "—" : (e.missing_kinds.join(", ") || "the export itself")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {!complete && (
        <p className="font-body text-[11px] text-muted-foreground max-w-3xl">
          Each missing row is one more export of the SAME report — same Bid-Bid spread, same
          Pounds Sterling, same <span className="font-mono">Custom Period Performance</span> value
          column — changing only the <span className="font-mono">from</span> date. Until they
          land, every cell for those periods renders an honest{" "}
          <span className="font-mono">not_found</span> rather than a number.
        </p>
      )}
    </section>
  );
}

export default function IntakeTab() {
  const { cycle, ir } = useOutletContext<CycleContext>();
  const access = useReportingAccess();
  const gs = ir?.cycle?.golden_source;
  const period = ir?.cycle?.period;
  const currentIdx = STAGES.indexOf((cycle.current_stage as CycleStage) ?? "snapshot");
  // Collapsed by default. The combined export is THE drop point; this is the back door for the
  // three kinds that are not in it.
  const [showOtherFiles, setShowOtherFiles] = useState(false);

  return (
    <div className="space-y-6">
      {/* ── Golden-CSV drop point ─────────────────────────────────────────── */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Drop the combined export</h3>
        <p className="font-body text-xs text-muted-foreground max-w-3xl">
          Every number in this cycle traces back to this file. It is read and classified in your
          browser first — you see the counts, the benchmark reconciliation and anything left over
          BEFORE a single byte is uploaded, and a file that fails a structural check cannot be
          confirmed at all. Once confirmed it is hashed on arrival, stored unchanged, and checked
          again server-side against its own declared period and currency; a file that fails any of
          those checks is refused whole, never partly landed.
        </p>
        <CombinedCsvDropZone
          cycleId={cycle.id}
          periodLabel={cycle.period_label}
          asOfDate={cycle.as_of_date}
        />
      </section>

      {/* ── What is still outstanding, stated rather than discovered later ──── */}
      <FeExportCensusPanel periodLabel={cycle.period_label} canRead={access.canReview} />

      {/* ── The back door, deliberately demoted ───────────────────────────────
          The combined performance export does NOT contain the opening holdings snapshot, the
          in-period holdings changes, or the broker/administrator trades — those come from the
          blotter, not from FE. reporting_snapshot_precheck requires all three for a
          monthly-interim cycle, so deleting the only surface that can land them would have
          quietly broken the cycle while looking like a simplification. It keeps the per-file
          kind picker because for THOSE files the kind genuinely is not derivable from an FE
          preamble that they do not have. It is collapsed, and it is not where anyone should
          start. */}
      <section className="space-y-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground"
          onClick={() => setShowOtherFiles((v) => !v)}
          aria-expanded={showOtherFiles}
        >
          {showOtherFiles ? (
            <ChevronDown className="h-3.5 w-3.5 mr-1" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 mr-1" aria-hidden />
          )}
          Holdings, trades and other non-FE files
        </Button>
        {showOtherFiles && (
          <div className="space-y-2 pl-1">
            <p className="font-body text-xs text-muted-foreground max-w-3xl">
              The opening holdings snapshot, the in-period holdings changes and the
              broker/administrator trades are not in the FE performance export — they come from the
              blotter. A cycle cannot pass its snapshot check without them. These files carry no FE
              preamble to identify themselves from, so this is the one place the intake still asks
              what a file is — and it asks about those three files only. The FE performance export
              cannot be landed from here; it goes through the combined drop point above, which is
              the only surface that checks it.
            </p>
            <GoldenDropZone
              cycleId={cycle.id}
              periodLabel={cycle.period_label}
              asOfDate={cycle.as_of_date}
              allowedKinds={BLOTTER_KINDS}
            />
          </div>
        )}
      </section>

      {/* ── The landed sources, and the attest control ────────────────────────
          THE GAP THIS CLOSES. Attestation used to be offered ONLY from the transient "what was
          landed" panel inside CombinedCsvDropZone, which renders off local React state. A
          reload — i.e. any new session — destroyed every attest button on this tab, and there
          was no other surface anywhere that could attest an already-landed source. Sources sat
          landed and unattested with no way to sign them, which is precisely what
          reporting_snapshot_precheck was refusing the cycle for. This panel reads the sources
          back from the server, so the control is here whenever the sources are. */}
      <GoldenSourcesPanel periodLabel={cycle.period_label} cycleId={cycle.id} />

      {/* Golden source */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Golden source</h3>
        {gs ? (
          <Card className="p-4">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-body text-sm">
              <dt className="text-muted-foreground">Table</dt>
              <dd className="text-foreground">{gs.fe_table}</dd>
              <dt className="text-muted-foreground">Period</dt>
              <dd className="text-foreground">{gs.period_label}</dd>
              <dt className="text-muted-foreground">Basis</dt>
              <dd className="text-foreground">{gs.basis}</dd>
              <dt className="text-muted-foreground">CSV ref</dt>
              <dd className="text-foreground">{gs.csv_ref}</dd>
              <dt className="text-muted-foreground">Accepted</dt>
              <dd className={cn(gs.accepted_at ? "text-primary" : "text-amber-600 dark:text-amber-400")}>
                {gs.accepted_at ? `${formatDay(gs.accepted_at)} by ${gs.accepted_by ?? "—"}` : "Not yet accepted"}
              </dd>
            </dl>
          </Card>
        ) : (
          <EmptyState
            title="The cycle has not been computed yet, so no golden source is pinned."
            body="This panel reflects reporting_cycles.snapshot_ref, which the compute stage writes when it freezes the cycle onto a specific set of sources. It is empty until then — it is NOT a statement that nothing has landed. What HAS landed is listed under 'Landed sources for this period' above."
          />
        )}
      </section>

      {/* Period identity */}
      {period && (
        <section className="space-y-2">
          <h3 className="font-heading text-base font-medium text-foreground">Period</h3>
          <Card className="p-4">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 font-body text-sm">
              <dt className="text-muted-foreground">Type</dt>
              <dd className="text-foreground">{period.type}</dd>
              <dt className="text-muted-foreground">Window</dt>
              <dd className="text-foreground">
                {formatDay(period.window_start)} → {formatDay(period.window_end)}
              </dd>
              <dt className="text-muted-foreground">As of</dt>
              <dd className="text-foreground">{formatDay(period.as_of_date)}</dd>
              <dt className="text-muted-foreground">Webinar month</dt>
              <dd className="text-foreground">
                {period.is_webinar_month ? "Yes" : "No"}
                <span className="text-muted-foreground text-xs">
                  {" "}
                  — derived from the period type (the webinar month is the quarterly month)
                </span>
              </dd>
            </dl>
          </Card>
        </section>
      )}

      {/* State-machine cursor */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Pipeline stage</h3>
        <Card className="p-4">
          <ol className="flex flex-wrap items-center gap-x-2 gap-y-2">
            {STAGES.map((s, i) => {
              const done = i < currentIdx;
              const active = i === currentIdx;
              return (
                <li key={s} className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 font-body text-sm px-2 py-1 rounded-md",
                      active
                        ? "bg-primary text-primary-foreground"
                        : done
                          ? "text-primary"
                          : "text-muted-foreground",
                    )}
                  >
                    {done ? <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> : <Circle className="h-3.5 w-3.5" aria-hidden />}
                    {s}
                  </span>
                  {i < STAGES.length - 1 && <span className="text-muted-foreground/40">→</span>}
                </li>
              );
            })}
          </ol>
          {cycle.last_error && (
            <PastRunError
              lastError={cycle.last_error}
              recordedAt={cycle.updated_at}
              periodLabel={cycle.period_label}
            />
          )}
          <p className="font-body text-[11px] text-muted-foreground mt-3">
            The cursor is moved by reporting-cycle-worker, one stage per tick, once a run is
            queued below. advance_reporting_stage remains available as a manual override but is
            deliberately not wired into this shell — a human nudging the cursor past a stage the
            machine has not actually completed is how a cycle reaches the gate with nothing
            behind it.
          </p>
        </Card>

        {/* The ignition. Gated internally on useReportingAccess and confirmed in a dialog —
            a run costs money on the compute and render legs. */}
        <RunCycleControl
          cycleId={cycle.id}
          periodLabel={cycle.period_label}
          status={cycle.status}
          currentStage={cycle.current_stage}
          tickCount={(cycle as any).tick_count}
          stageCap={(cycle as any).stage_cap}
          stageOutputs={(cycle as any).stage_outputs}
        />
      </section>
    </div>
  );
}
